
/**
 * Format a number as currency
 * @param value - The number to format
 * @param currency - The currency code, defaults to USD
 * @returns Formatted currency string
 */
export function formatCurrency(
  value: number,
  currency: string = "USD"
): string {
  // Handle proper formatting for different magnitudes
  if (Math.abs(value) >= 1000000) {
    return `$${(value / 1000000).toFixed(2)}M`;
  } else if (Math.abs(value) >= 1000) {
    return `$${(value / 1000).toFixed(1)}K`;
  }

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Format a number as a percentage
 * @param value - The decimal value to format as percentage (e.g., 0.15 for 15%)
 * @returns Formatted percentage string
 */
export function formatPercentage(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}

/**
 * Format a date according to the locale
 * @param date - Date to format
 * @param format - Optional format string, defaults to long format
 * @returns Formatted date string
 */
export function formatDate(
  date: Date,
  format: "short" | "medium" | "long" = "medium"
): string {
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: format === "short" ? "numeric" : "long",
    day: "numeric",
  };

  return new Intl.DateTimeFormat("en-US", options).format(date);
}

/**
 * Format a number with thousands separators and specified decimal places
 * @param value - The number to format
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted number string
 */
export function formatNumber(
  value: number,
  decimals: number = 2
): string {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}
